import * as React from 'react';

import { rankItem } from '@tanstack/match-sorter-utils';
import {
    type ColumnDef,
    type FilterFn,
    type TableOptions,
    type VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';

import { DataTableBody, type DataTableBodyProps } from 'src/registry/components/table/DataTableBody';
import { DataTableHead } from 'src/registry/components/table/DataTableHead';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { Table, TableBody, TableHead, TableHeader, TableRow } from 'src/registry/ui/Table';
import { cn } from 'src/utils/shadcn';

import type { DataTableControlsProps } from 'src/registry/components/table/DataTableControls';
import type { DataTablePaginationProps } from 'src/registry/components/table/DataTablePagination';
import type {
    DataTableColumnHeaderMeta,
    DataTableFiltersType,
    DataTableNewEntryProps,
    DataTablePaginationType,
    DataTableRowSelectionType,
    DataTableSearchType,
    DataTableSemantics,
    DataTableSortingType,
} from 'src/registry/components/table/types';
import type { GeneralResponse } from 'src/registry/types/queries';

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    // Rank the item
    const itemRank = rankItem(row.getValue(columnId), String(value));

    // Store the itemRank info
    addMeta({
        itemRank,
    });

    // Return if the item should be filtered in/out
    return itemRank.passed;
};

// Because we mix global filter which is done locally with column filters which are done on the server we have to
// provide a local function that always returns true

const blockFilter: FilterFn<any> = () => {
    return true;
};

interface DataTableProps<TData, TValue, TResponse = GeneralResponse<TData>>
    extends React.HTMLAttributes<HTMLDivElement>,
        Omit<TableOptions<TData>, 'data' | 'getCoreRowModel'> {
    columns: ColumnDef<TData, TValue>[];
    data?: TResponse;
    sorting?: DataTableSortingType;
    pagination?: DataTablePaginationType;
    filters?: DataTableFiltersType;
    search?: DataTableSearchType;
    rowSelection?: DataTableRowSelectionType;
    isMultiSelect?: boolean;
    semantics?: DataTableSemantics;
    isLoading?: boolean;
    isError?: boolean;
    errorUpdateCount?: number;
    refetch?: () => void;
    components?: {
        controls?: (props: DataTableControlsProps<TData>) => React.ReactNode;
        pagination?: (props: DataTablePaginationProps<TData>) => React.ReactNode;
        newEntry?: (props: DataTableNewEntryProps<TData>) => React.ReactNode;
    };
    dataTableBodyProps?: Omit<
        DataTableBodyProps<TData>,
        'table' | 'semantics' | 'isLoading' | 'isError' | 'columnLength'
    >;
    enableSearchParams?: boolean;
    enableLocalFilters?: boolean;
    onFilterSearch?: (columnId: string, value: string) => void;
}

export function DataTable<TData, TValue>({
    columns,
    data = { count: 0, next: null, previous: null, results: [] },
    sorting,
    pagination,
    filters,
    search,
    rowSelection,
    isMultiSelect = false,
    semantics,
    isLoading,
    isError,
    onFilterSearch,
    errorUpdateCount,
    refetch,
    className,
    components: { controls: ControlsComponent, pagination: PaginationComponent, newEntry: NewEntryComponent } = {},
    dataTableBodyProps,
    enableSearchParams = true,
    enableLocalFilters = true,
    ...options
}: DataTableProps<TData, TValue>) {
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
        semantics?.initialColumnVisibility ? semantics?.initialColumnVisibility : {},
    );
    const [lastSelectedIndex, setLastSelectedIndex] = React.useState<number | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const { clearRowSelection } = useRowSelection({ sidePanelName: semantics?.sidePanelName });

    React.useEffect(() => {
        // Reset row selection when pagination changes
        clearRowSelection();
    }, [clearRowSelection, pagination?.paginationState]);

    React.useEffect(() => {
        // Reset row and page selection when filters or sorting changes
        table.setPageIndex(0);
        clearRowSelection();
    }, [filters?.columnFiltersState, sorting?.sortingState, clearRowSelection, search?.globalFilterState]);

    const tableOptions: TableOptions<TData> = {
        data: data.results,
        columns,
        defaultColumn: {
            minSize: undefined,
            maxSize: undefined,
            filterFn: blockFilter,
            enableColumnFilter: false,
            enableHiding: false,
            enableSorting: false,
        },
        renderFallbackValue: '-',
        state: { columnVisibility },
        ...(enableLocalFilters ? { getFilteredRowModel: getFilteredRowModel() } : {}),
        onColumnVisibilityChange: setColumnVisibility,
        ...options,
        getCoreRowModel: getCoreRowModel(),
    };

    if (pagination?.paginationState) {
        tableOptions.pageCount = Math.ceil(data?.count / pagination.paginationState.pageSize);
        tableOptions.manualPagination = true;
        tableOptions.onPaginationChange = pagination.setPaginationState;

        if (tableOptions.state) {
            tableOptions.state.pagination = pagination.paginationState;
        }
    }

    if (sorting?.sortingState) {
        tableOptions.onSortingChange = sorting.setSortingState;
        tableOptions.filterFns = { block: blockFilter };

        if (tableOptions.state) {
            tableOptions.state.sorting = sorting.sortingState;
        }
    }

    if (filters?.columnFiltersState) {
        tableOptions.onColumnFiltersChange = filters.setColumnFiltersState;
        // we can piggyback the meta to store the state of the dropdowns
        tableOptions.meta = {
            columnFiltersShowDropdownState: filters.columnFiltersShowDropdownState,
            setColumnFiltersShowDropdownState: filters.setColumnFiltersShowDropdownState,
            setColumnFiltersState: filters.setColumnFiltersState,
        };

        if (tableOptions.state) {
            tableOptions.state.columnFilters = filters.columnFiltersState;
        }
    }

    if (search?.globalFilterState != null) {
        tableOptions.onGlobalFilterChange = search.setGlobalFilterState;
        tableOptions.globalFilterFn = fuzzyFilter;

        if (tableOptions.state) {
            tableOptions.state.globalFilter = search.globalFilterState;
        }
    }

    if (rowSelection?.rowSelectionState) {
        tableOptions.onRowSelectionChange = rowSelection.setRowSelectionState;
        tableOptions.enableMultiRowSelection = rowSelection.enableMultiRowSelection ?? true;

        if (tableOptions.state) {
            tableOptions.state.rowSelection = rowSelection.rowSelectionState;
        }
    }

    const table = useReactTable(tableOptions);

    // We want to update the search params, whenever our table state updates
    React.useEffect(() => {
        if (!enableSearchParams) return;
        const paginationState = table.getState().pagination;
        const sortingState = table.getState().sorting;

        if (paginationState.pageIndex) {
            searchParams.set('page', paginationState.pageIndex.toString());
        } else {
            searchParams.delete('page');
        }
        if (paginationState.pageSize) {
            searchParams.set('size', paginationState.pageSize.toString());
        } else {
            searchParams.delete('size');
        }
        if (sortingState && sortingState.length > 0) {
            searchParams.set('ordering', sortingState?.map((sort) => (sort.desc ? `-${sort.id}` : sort.id)).join(','));
        } else {
            searchParams.delete('ordering');
        }

        setSearchParams(searchParams, { replace: true });
    }, [enableSearchParams, searchParams, setSearchParams, table]);

    //Handles shift multi select
    React.useEffect(() => {
        const handler = (e: CustomEvent) => {
            const { rowIndex, shiftKey, checked, rowId } = e.detail;

            if (shiftKey && lastSelectedIndex !== null) {
                const start = Math.min(lastSelectedIndex, rowIndex);
                const end = Math.max(lastSelectedIndex, rowIndex);

                const rowsInRange = table.getRowModel().rows.slice(start, end + 1);

                const newSelection: Record<string, boolean> = {};
                rowsInRange.forEach((r) => {
                    newSelection[r.id] = checked;
                });

                rowSelection?.setRowSelectionState?.((prev: any) => ({
                    ...prev,
                    ...newSelection,
                }));
            } else {
                table.getRow(rowId)?.toggleSelected(checked);
            }

            setLastSelectedIndex(rowIndex);
        };

        window.addEventListener('row-checkbox-click', handler as any);
        return () => window.removeEventListener('row-checkbox-click', handler as any);
    }, [lastSelectedIndex, rowSelection, table]);

    const gridTemplateColumns = table
        .getVisibleFlatColumns()
        .map((column) => {
            const { minSize, maxSize, id, meta } = column.columnDef;
            const isLongText = (meta as DataTableColumnHeaderMeta<TData, TValue>)?.isLongText;
            const min = minSize ? `${minSize}px` : 'min-content';
            const max = maxSize ? `${maxSize}px` : 'max-content';
            if (isLongText) return 'minmax(0, 1fr)';
            if (id === 'index') return `minmax(min-content, 1ch)`;
            if (minSize || maxSize) return `minmax(${min}, ${max})`;
            return '1fr';
        })
        .join(' ');

    // We want to be generally able to update the table/refetch the data once something
    // changes on the parameters side or the data itself. e.g. sorting, pagination, filters, etc.
    const onDataChange = () => {
        if (refetch) refetch();
    };

    return (
        <div className={cn('flex flex-1 flex-col gap-4', className)}>
            <div className={cn('flex justify-between', !ControlsComponent && 'sm:-mb-4')}>
                {ControlsComponent && (
                    <ControlsComponent semantics={semantics} table={table} isLoading={isLoading} isError={isError} onFilterSearch={onFilterSearch} />
                )}
                {NewEntryComponent && (
                    <NewEntryComponent
                        semantics={semantics}
                        table={table}
                        onSubmit={onDataChange}
                        className={!ControlsComponent ? 'sm:-mt-16' : undefined}
                    />
                )}
            </div>
            <div className={cn('overflow-auto rounded-md border shadow-xs dark:border-gray-700')}>
                {semantics?.title && <DataTableHead semantics={semantics} count={data?.count} isLoading={isLoading} />}
                <Table style={{ gridTemplateColumns }}>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow semantics={Boolean(semantics)} key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} semantics={Boolean(semantics)}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        <DataTableBody<TData>
                            table={table}
                            columnLength={columns.length}
                            isLoading={isLoading}
                            isError={isError}
                            semantics={semantics}
                            errorUpdateCount={errorUpdateCount}
                            enableRowClick={!isMultiSelect}
                            {...dataTableBodyProps}
                        />
                    </TableBody>
                </Table>
            </div>
            {PaginationComponent && pagination && (
                <PaginationComponent count={data?.count} table={table} isLoading={isLoading} isError={isError} />
            )}
        </div>
    );
}
